/**
 * ExercisePickerModal — modal triple-pane para elegir ejercicios.
 *
 * Layout desktop:
 *   ┌──────────┬─────────────┬──────────────────────┐
 *   │ Filtros  │  Lista      │  Detalle del ejer.   │
 *   │ (200px)  │ (320-420px) │  (1fr) — GIF grande, │
 *   │          │             │  instrucciones,      │
 *   │ + Custom │             │  botón "Agregar"     │
 *   └──────────┴─────────────┴──────────────────────┘
 *
 * Mobile: stack vertical, detalle como overlay deslizable.
 *
 * Features:
 * - Filtros agrupados por sinónimos (Espalda = espalda + dorsal + lumbares + ...)
 * - Lista vertical paginada (50 por página)
 * - Click en ejercicio → muestra detalle a la derecha (no selecciona aún)
 * - Botón "Agregar a la rutina" en el detalle
 * - "Crear ejercicio personalizado" para ítems que no están en el catálogo
 * - Comparación de strings con normalize() (NFD/NFC tolerante)
 */
import { useEffect, useMemo, useState } from "react";
import { useCatalog } from "../../contexts/CatalogContext";
import { exerciseGifUrl, type Exercise } from "../../api/exercises";
import { Icon } from "../../lib/Icon";
import { normalize } from "../../lib/normalizeText";
import { MUSCLE_GROUPS } from "../../lib/muscleGroups";

interface ExercisePickerModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (ex: Exercise) => void;
  /**
   * Si se setea, el picker arranca FIJADO a ese grupo muscular (ej. "Cardio")
   * y oculta el sidebar de filtros para mantener la UX enfocada. Útil para
   * la sección de cardio, que solo debe permitir ejercicios cardiovasculares.
   */
  lockGroup?: string;
}

const ALL_MUSCLES = "__all__";
const PAGE_SIZE = 50;
// MUSCLE_GROUPS movido a `src/lib/muscleGroups.ts` para compartir con
// LibraryScreen (antes había 2 copias divergentes — bug histórico).

export function ExercisePickerModal({ open, onClose, onPick, lockGroup }: ExercisePickerModalProps) {
  const { exercises, loaded, loading, ensureLoaded, search } = useCatalog();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  // Si hay `lockGroup` el filtro arranca y se queda fijo en ese grupo. Sin
  // lockGroup el comportamiento es el normal ("Todos" por default).
  const [selectedGroup, setSelectedGroup] = useState<string>(lockGroup || ALL_MUSCLES);
  const [limit, setLimit] = useState(PAGE_SIZE);
  /** Ejercicio actualmente mostrado en el panel de detalle (no seleccionado aún). */
  const [previewing, setPreviewing] = useState<Exercise | null>(null);
  /** Modal "Crear ejercicio personalizado" (cuando el coach quiere algo que no está). */
  const [customForm, setCustomForm] = useState<{ name: string; muscle: string; equipment: string } | null>(null);
  /** Toggle: si true, oculta ejercicios sin GIF (~86 de 1448 = 6%). Default false. */
  const [onlyWithVideo, setOnlyWithVideo] = useState(false);

  useEffect(() => { if (open) ensureLoaded(); }, [open, ensureLoaded]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q), 200);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (customForm) setCustomForm(null);
        else if (previewing) setPreviewing(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, previewing, customForm]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setDebouncedQ("");
      // Si hay lockGroup mantenemos el filtro forzado; sino reseteamos a todos.
      setSelectedGroup(lockGroup || ALL_MUSCLES);
      setLimit(PAGE_SIZE);
      setPreviewing(null);
      setCustomForm(null);
    } else if (lockGroup) {
      // Al abrirse con lockGroup forzamos el filtro (por si quedó de una
      // apertura anterior sin lock).
      setSelectedGroup(lockGroup);
    }
  }, [open, lockGroup]);

  useEffect(() => { setLimit(PAGE_SIZE); }, [debouncedQ, selectedGroup]);

  const chips = useMemo(() => {
    if (!loaded) return [];
    const counts = new Map<string, number>();
    for (const ex of exercises) {
      const m = normalize(ex.muscle || ex.primary_muscle || ex.category);
      if (!m) continue;
      counts.set(m, (counts.get(m) || 0) + 1);
    }
    const groupedMuscles = new Set<string>();
    const result: { key: string; label: string; count: number }[] = [];
    for (const g of MUSCLE_GROUPS) {
      let total = 0;
      for (const m of g.members) {
        total += counts.get(m) || 0;
        groupedMuscles.add(m);
      }
      if (total > 0) result.push({ key: g.label, label: g.label, count: total });
    }
    const orphanCounts = new Map<string, { display: string; count: number }>();
    for (const ex of exercises) {
      const raw = ex.muscle || ex.primary_muscle || ex.category;
      if (!raw) continue;
      const norm = normalize(raw);
      if (groupedMuscles.has(norm)) continue;
      if (!orphanCounts.has(norm)) orphanCounts.set(norm, { display: raw, count: 0 });
      orphanCounts.get(norm)!.count++;
    }
    for (const [norm, info] of orphanCounts) {
      result.push({ key: norm, label: info.display, count: info.count });
    }
    return result;
  }, [exercises, loaded]);

  const muscleFilter = useMemo<string[] | undefined>(() => {
    if (selectedGroup === ALL_MUSCLES) return undefined;
    const group = MUSCLE_GROUPS.find((g) => g.label === selectedGroup);
    if (group) return group.members;
    return [selectedGroup];
  }, [selectedGroup]);

  const list = useMemo<Exercise[]>(() => {
    if (!loaded) return [];
    let result = search(debouncedQ, muscleFilter);
    // Filtro duro si el coach lo activa
    if (onlyWithVideo) {
      result = result.filter((ex) => exerciseGifUrl(ex));
    }
    // Sort: ejercicios CON video primero (más útiles para mostrar técnica al cliente),
    // sin video al final. Estable: respeta orden interno del backend.
    return [...result].sort((a, b) => {
      const aHas = exerciseGifUrl(a) ? 1 : 0;
      const bHas = exerciseGifUrl(b) ? 1 : 0;
      return bHas - aHas;
    });
  }, [search, debouncedQ, muscleFilter, loaded, onlyWithVideo]);

  /** Conteo de cuántos del list actual tienen video — para el label del toggle. */
  const withVideoCount = useMemo(() => {
    if (!loaded) return 0;
    return list.filter((ex) => exerciseGifUrl(ex)).length;
  }, [list, loaded]);

  const visible = list.slice(0, limit);

  const handleConfirm = (ex: Exercise) => {
    onPick(ex);
  };

  const handleCustomSubmit = () => {
    if (!customForm || !customForm.name.trim()) return;
    // Construye un Exercise sintético — sin libId, sin gif. El gifUrl quedará vacío
    // (el coach puede subirlo después o dejarlo así).
    const custom: Exercise = {
      id: `custom_${Date.now()}`,
      name: customForm.name.trim(),
      muscle: customForm.muscle.trim() || "Personalizado",
      equipment: customForm.equipment.trim() || null,
      video_url: null,
      gif_url: null,
      description: null,
    };
    onPick(custom);
    setCustomForm(null);
  };

  if (!open) return null;

  return (
    <div className="al-modal-backdrop" onClick={onClose}>
      <div className="al-modal al-ex-picker" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Biblioteca de ejercicios
            </h3>
            <div className="tiny muted">
              {!loaded && loading
                ? "Cargando catálogo…"
                : `${list.length.toLocaleString("es-CO")} ejercicios${
                    selectedGroup !== ALL_MUSCLES ? ` en ${selectedGroup}` : ""
                  }`}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Cerrar">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div
          className={"al-ex-picker-body" + (lockGroup ? " is-locked" : "")}
          style={{ position: "relative" }}
        >
          {/* Sidebar: filtros + crear personalizado. Se oculta cuando el
              picker está fijado a un grupo (lockGroup) — la sección
              consumidora ya decidió el tipo, no hace falta cambiarlo. */}
          {chips.length > 0 && !lockGroup && (
            <aside className="al-ex-picker-side">
              <div className="al-ex-picker-side-title">FILTRAR POR MÚSCULO</div>
              <div className="al-ex-chips">
                <button
                  className={"al-ex-chip" + (selectedGroup === ALL_MUSCLES ? " active" : "")}
                  onClick={() => setSelectedGroup(ALL_MUSCLES)}
                  type="button"
                >
                  <span>Todos</span>
                  <span style={{ opacity: 0.7, fontSize: 11 }}>{exercises.length}</span>
                </button>
                {chips.map((c) => (
                  <button
                    key={c.key}
                    className={"al-ex-chip" + (selectedGroup === c.key ? " active" : "")}
                    onClick={() => setSelectedGroup(c.key)}
                    type="button"
                  >
                    <span>{c.label}</span>
                    <span style={{ opacity: 0.7, fontSize: 11 }}>{c.count}</span>
                  </button>
                ))}
              </div>
              {/* Toggle: solo ejercicios con video animado */}
              <label className="al-ex-toggle" title="Esconder ejercicios sin GIF de demostración">
                <input
                  type="checkbox"
                  checked={onlyWithVideo}
                  onChange={(e) => setOnlyWithVideo(e.target.checked)}
                />
                <span>
                  <b>Solo con demo</b>
                  <span className="tiny" style={{ display: "block", opacity: 0.7 }}>
                    {withVideoCount} con video
                  </span>
                </span>
              </label>

              <button
                className="al-ex-picker-side-action"
                type="button"
                onClick={() => setCustomForm({ name: "", muscle: "", equipment: "" })}
                title="Crear un ejercicio personalizado que no está en la biblioteca"
              >
                + Crear personalizado
              </button>
            </aside>
          )}

          {/* Main: search + lista */}
          <main className="al-ex-picker-main">
            <div className="al-ex-search">
              <Icon name="search" size={16} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar press, sentadilla, dominada…"
                autoFocus
              />
              {q && (
                <button className="link-btn" onClick={() => setQ("")} type="button">
                  limpiar
                </button>
              )}
            </div>

            <div className="al-ex-grid">
              {!loaded && loading && (
                <div className="al-ex-empty">Cargando catálogo de ejercicios…</div>
              )}
              {loaded && list.length === 0 && (
                <div className="al-ex-empty">
                  Sin resultados{debouncedQ ? ` para "${debouncedQ}"` : ""}.<br/>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: 12 }}
                    type="button"
                    onClick={() => setCustomForm({ name: debouncedQ, muscle: "", equipment: "" })}
                  >
                    + Crear "{debouncedQ || "ejercicio personalizado"}"
                  </button>
                </div>
              )}
              {visible.map((ex) => {
                const url = exerciseGifUrl(ex);
                const isActive = previewing?.id === ex.id;
                return (
                  <button
                    key={ex.id}
                    type="button"
                    className={"al-ex-card" + (isActive ? " is-active" : "") + (!url ? " no-video" : "")}
                    onClick={() => setPreviewing(ex)}
                  >
                    <div className="al-ex-card-thumb">
                      {url ? (
                        // Sin `loading="lazy"`: el picker está dentro de un modal
                        // con su propia scroll-area; el IntersectionObserver del
                        // browser no siempre dispara correctamente sobre ese
                        // contenedor y algunos GIFs quedan en blanco aunque el
                        // archivo existe (ver modal de preview, que sí los muestra).
                        <img
                          src={url}
                          alt={ex.name}
                          decoding="async"
                          onError={(e) => {
                            const el = e.currentTarget;
                            el.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="al-ex-card-fallback">
                          <Icon name="hammer" size={20} />
                        </div>
                      )}
                    </div>
                    <div className="al-ex-card-body">
                      <div className="al-ex-card-name">{ex.name}</div>
                      <div className="al-ex-card-tag">
                        {ex.muscle || ex.primary_muscle || "—"}
                        {ex.equipment ? ` · ${ex.equipment}` : ""}
                        {!url && <span className="al-ex-no-video-tag"> · Sin demo</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {visible.length < list.length && (
              <div className="al-ex-more">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setLimit((l) => l + PAGE_SIZE)}
                  type="button"
                >
                  Cargar más ({list.length - visible.length} restantes)
                </button>
              </div>
            )}
          </main>

          {/* Detalle: sub-modal centrado SOBRE el picker — no compite por ancho */}
          {previewing && (
            <div
              className="al-ex-picker-detail"
              onClick={() => setPreviewing(null)}
            >
              <div
                className="al-ex-picker-detail-card"
                onClick={(e) => e.stopPropagation()}
              >
                <ExerciseDetail
                  exercise={previewing}
                  onAdd={() => handleConfirm(previewing)}
                  onBack={() => setPreviewing(null)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Modal "Crear ejercicio personalizado" — overlay sobre el picker */}
        {customForm && (
          <div className="al-ex-custom-overlay" onClick={() => setCustomForm(null)}>
            <div className="al-ex-custom-modal" onClick={(e) => e.stopPropagation()}>
              <div className="al-modal-hd">
                <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
                  Crear ejercicio personalizado
                </h3>
                <button className="icon-btn" onClick={() => setCustomForm(null)} title="Cerrar">
                  <Icon name="x" size={16} />
                </button>
              </div>
              <div style={{ padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div className="tiny muted">
                  Para ejercicios que no están en la biblioteca. No tendrán GIF animado pero podés agregar la descripción.
                </div>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span className="tiny" style={{ fontWeight: 600 }}>Nombre del ejercicio *</span>
                  <input
                    type="text"
                    className="al-input"
                    value={customForm.name}
                    onChange={(e) => setCustomForm({ ...customForm, name: e.target.value })}
                    placeholder="Ej. Burpee con flexión"
                    autoFocus
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span className="tiny" style={{ fontWeight: 600 }}>Grupo muscular</span>
                  <input
                    type="text"
                    className="al-input"
                    value={customForm.muscle}
                    onChange={(e) => setCustomForm({ ...customForm, muscle: e.target.value })}
                    placeholder="Ej. Full body, Pecho, Cardio…"
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span className="tiny" style={{ fontWeight: 600 }}>Equipamiento (opcional)</span>
                  <input
                    type="text"
                    className="al-input"
                    value={customForm.equipment}
                    onChange={(e) => setCustomForm({ ...customForm, equipment: e.target.value })}
                    placeholder="Ej. Mancuernas, peso corporal…"
                  />
                </label>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button className="btn btn-ghost" onClick={() => setCustomForm(null)} type="button">
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleCustomSubmit}
                    disabled={!customForm.name.trim()}
                    type="button"
                    style={{ flex: 1 }}
                  >
                    Crear y agregar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// ExerciseDetail — panel lateral con GIF grande + instrucciones + add
// ----------------------------------------------------------------------

interface ExerciseDetailProps {
  exercise: Exercise;
  onAdd: () => void;
  onBack: () => void;
}

function ExerciseDetail({ exercise, onAdd, onBack }: ExerciseDetailProps) {
  const url = exerciseGifUrl(exercise);
  const instructions = exercise.instructions || exercise.description;

  return (
    <>
      <div className="al-ex-detail-hero">
        {url ? (
          <img src={url} alt={exercise.name} />
        ) : (
          <div style={{ color: "var(--fg-faint)", textAlign: "center", padding: 32 }}>
            <Icon name="hammer" size={48} />
            <div className="tiny" style={{ marginTop: 12 }}>Sin GIF disponible</div>
          </div>
        )}
      </div>
      <div className="al-ex-detail-body">
        {/* Close button (esquina sup-der) — vive fuera del scroll para
            seguir visible aunque el coach scrollee el detalle. */}
        <button
          className="al-ex-detail-close"
          onClick={onBack}
          type="button"
          title="Cerrar detalle"
          aria-label="Cerrar"
        >
          <Icon name="x" size={14} />
        </button>

        {/* Contenido scrolleable. Cuando las instrucciones son largas, este
            div hace scroll INTERNO y los botones de acción quedan fijos en
            el footer (siempre visibles). */}
        <div className="al-ex-detail-scroll">
          <div>
            <div className="al-ex-detail-name" style={{ paddingRight: 36 }}>
              {exercise.name}
            </div>
            <div className="al-ex-detail-meta" style={{ marginTop: 8 }}>
              {exercise.muscle && <span className="al-ex-detail-meta-tag">{exercise.muscle}</span>}
              {exercise.equipment && <span className="al-ex-detail-meta-tag">{exercise.equipment}</span>}
              {exercise.difficulty && <span className="al-ex-detail-meta-tag">{exercise.difficulty}</span>}
              {exercise.secondary_muscles && (
                <span className="al-ex-detail-meta-tag" title="Músculos secundarios">
                  + {exercise.secondary_muscles}
                </span>
              )}
            </div>
          </div>

          {instructions && (
            <div>
              <div className="al-ex-detail-section-label">EJECUCIÓN</div>
              <div className="al-ex-detail-instructions">{instructions}</div>
            </div>
          )}

          {!instructions && (
            <div className="tiny muted" style={{ fontStyle: "italic" }}>
              Sin instrucciones disponibles.
            </div>
          )}
        </div>

        {/* Footer fijo: los botones SIEMPRE visibles, no importan los párrafos. */}
        <div className="al-ex-detail-actions">
          <button className="btn btn-secondary" onClick={onBack} type="button">
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={onAdd} type="button">
            <Icon name="plus" size={12} /> Agregar a la rutina
          </button>
        </div>
      </div>
    </>
  );
}
